/**
 * Doserne Academy — Course Acknowledgement + Signature page.
 *
 * Shown after the last quiz is passed. Employee must:
 *   1) tick a checkbox confirming they read the declaration
 *   2) type their full legal name
 *   3) draw a handwritten signature in the signature pad
 * The submit button stays disabled until all three are provided.
 *
 * On submit the backend creates an immutable acknowledgement + a certificate
 * (idempotent per course_version) and this page navigates to the "certificate
 * issued" view (currently the course detail page shows the cert card).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, PenSquare, RotateCcw, ShieldCheck, Loader2, ArrowLeft, Award, GraduationCap, ArrowRight } from "lucide-react";
import DoserneShell from "./DoserneShell";
import { useLang, pick } from "./i18n";
import { useDoserneAuth } from "./doserneAuth";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const T = {
  no: {
    kicker: "Endelig bekreftelse",
    title: "Signer og motta kursbeviset",
    intro: "Før vi utsteder ditt Doserne Academy-kursbevis må du bekrefte deklarasjonen nedenfor, skrive inn ditt fulle juridiske navn og signere med hånd.",
    course: "Kurs",
    completed: "Fullført",
    score: "Resultat",
    version: "Kursversjon",
    declLead: "Bekreftelse fra deg som ansatt",
    consent: "Jeg har lest og bekrefter erklæringen ovenfor.",
    nameLabel: "Fullt juridisk navn",
    namePh: "F.eks. Kari Nordmann",
    sigLabel: "Håndskrevet signatur",
    sigHint: "Bruk finger eller mus for å signere i feltet nedenfor.",
    clear: "Tøm signatur",
    submit: "Bekreft og motta kursbevis",
    submitting: "Signerer …",
    doneKicker: "Kursbeviset ditt er utstedt",
    doneTitle: "Gratulerer! Kurset er fullført.",
    viewCert: "Vis kursbevis (PDF)",
    backToCourse: "Tilbake til kurset",
    goCourses: "Gå til kurs",
    nextCourse: "Neste kurs",
    error: "Kunne ikke signere. Prøv igjen.",
  },
  ro: {
    kicker: "Confirmare finală",
    title: "Semnează și primește certificatul",
    intro: "Înainte de emiterea certificatului Doserne Academy trebuie să confirmi declarația de mai jos, să îți scrii numele complet legal și să semnezi olograf.",
    course: "Curs",
    completed: "Finalizat",
    score: "Rezultat",
    version: "Versiune curs",
    declLead: "Confirmarea ta ca angajat",
    consent: "Am citit și confirm declarația de mai sus.",
    nameLabel: "Nume complet legal",
    namePh: "Ex. Ionescu Andrei Marius",
    sigLabel: "Semnătură olografă",
    sigHint: "Folosește degetul sau mouse-ul pentru a semna în câmpul de mai jos.",
    clear: "Șterge semnătura",
    submit: "Confirm și primesc certificatul",
    submitting: "Se semnează …",
    doneKicker: "Certificatul tău a fost emis",
    doneTitle: "Felicitări! Cursul este finalizat.",
    viewCert: "Vezi certificatul (PDF)",
    backToCourse: "Înapoi la curs",
    goCourses: "Mergi la Cursuri",
    nextCourse: "Următorul curs",
    error: "Nu s-a putut semna. Încearcă din nou.",
  },
  en: {
    kicker: "Final acknowledgement",
    title: "Sign to receive your certificate",
    intro: "Before we issue your Doserne Academy certificate you must confirm the declaration below, type your full legal name and add a handwritten signature.",
    course: "Course",
    completed: "Completed",
    score: "Result",
    version: "Course version",
    declLead: "Your acknowledgement as an employee",
    consent: "I have read and confirm the declaration above.",
    nameLabel: "Full legal name",
    namePh: "e.g. John Anderson",
    sigLabel: "Handwritten signature",
    sigHint: "Use your finger or mouse to sign inside the field below.",
    clear: "Clear signature",
    submit: "Confirm and receive certificate",
    submitting: "Signing …",
    doneKicker: "Your certificate has been issued",
    doneTitle: "Congratulations! The course is complete.",
    viewCert: "View certificate (PDF)",
    backToCourse: "Back to course",
    goCourses: "Go to Courses",
    nextCourse: "Next course",
    error: "Could not sign. Please try again.",
  },
};

// The exact declaration text, in every language. MUST stay identical to the
// backend copy in services/doserne_certificate.py — the backend also stores
// the version + sha256 so any drift is auditable.
const DECLARATIONS = {
  no: "Jeg bekrefter at jeg har gjennomgått og lest hele dette kurset, at jeg har forstått informasjonen, sikkerhetsinstruksjonene og bedriftens regler som er presentert, og at jeg har hatt anledning til å be om oppklaringer. Jeg bekrefter at jeg selv har utført testene og at jeg ikke har hoppet over innhold uten å lese det. Jeg forplikter meg til å følge instruksjonene og reglene som er presentert, og til å stoppe arbeidet og be om hjelp når en situasjon ikke er trygg eller ikke er tydelig. Jeg forstår at denne bekreftelsen ikke erstatter praktisk opplæring, formelle kvalifikasjoner eller den tilsyn som kreves for enkelte aktiviteter.",
  ro: "Confirm că am parcurs și am citit integral acest curs, că am înțeles informațiile, instrucțiunile de siguranță și regulile companiei prezentate și că am avut posibilitatea să cer clarificări. Confirm că am completat personal testele și că nu am trecut prin conținut fără să îl citesc. Mă angajez să respect instrucțiunile și regulile prezentate și să opresc lucrul și să cer ajutor atunci când o situație nu este sigură sau nu este clară. Înțeleg că această confirmare nu înlocuiește instruirea practică, calificările sau supravegherea cerute pentru anumite activități.",
  en: "I confirm that I have gone through and read this course in full, that I have understood the information, safety instructions and company rules presented, and that I have had the opportunity to ask for clarifications. I confirm that I personally completed the tests and that I did not skip through the content without reading it. I commit to following the instructions and rules presented and to stopping work and asking for help whenever a situation is not safe or is not clear. I understand that this confirmation does not replace practical training, formal qualifications or the supervision required for certain activities.",
};

// -----------------------------------------------------------------------------
// Minimal canvas signature pad (no extra deps).
// -----------------------------------------------------------------------------
function SignaturePad({ onChange, resetKey }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const hasInk = useRef(false);

  const scale = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.max(1, Math.floor(rect.width * dpr));
    c.height = Math.max(1, Math.floor(rect.height * dpr));
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  useEffect(() => {
    scale();
    hasInk.current = false;
    onChange(null);
    const on = () => scale();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const getPoint = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPoint(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    hasInk.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) {
      const data = canvasRef.current.toDataURL("image/png");
      onChange(data);
    }
  };

  // Attach native DOM listeners so events fire even for synthetic mouse
  // drivers (Playwright, etc.) that do not always bubble through React's
  // synthetic event system on <canvas>.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const onDown = (e) => start(e);
    const onMove = (e) => move(e);
    const onUp = () => end();
    c.addEventListener("pointerdown", onDown);
    c.addEventListener("pointermove", onMove);
    c.addEventListener("pointerup", onUp);
    c.addEventListener("pointerleave", onUp);
    c.addEventListener("mousedown", onDown);
    c.addEventListener("mousemove", onMove);
    c.addEventListener("mouseup", onUp);
    c.addEventListener("touchstart", onDown, { passive: false });
    c.addEventListener("touchmove", onMove, { passive: false });
    c.addEventListener("touchend", onUp);
    return () => {
      c.removeEventListener("pointerdown", onDown);
      c.removeEventListener("pointermove", onMove);
      c.removeEventListener("pointerup", onUp);
      c.removeEventListener("pointerleave", onUp);
      c.removeEventListener("mousedown", onDown);
      c.removeEventListener("mousemove", onMove);
      c.removeEventListener("mouseup", onUp);
      c.removeEventListener("touchstart", onDown);
      c.removeEventListener("touchmove", onMove);
      c.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="signature-pad"
      className="w-full h-40 sm:h-48 rounded-2xl border-2 border-dashed border-slate-300 bg-white touch-none"
    />
  );
}

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function DoserneCourseAck() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const { authHeader } = useDoserneAuth();
  const t = T[lang] || T.no;
  const declaration = DECLARATIONS[lang] || DECLARATIONS.no;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [checked, setChecked] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [signature, setSignature] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [issuedCert, setIssuedCert] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/kurs/${code}/completion-status`, authHeader());
      setStatus(data);
      if (data.state === "completed" && data.certificate) {
        setIssuedCert(data.certificate);
      }
    } catch (e) {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const canSubmit = useMemo(
    () => checked && typedName.trim().length >= 2 && !!signature && !submitting,
    [checked, typedName, signature, submitting]
  );

  // Open the certificate PDF WITH auth (the endpoint requires a member token,
  // so a plain link 401s → fetch as a blob and open the object URL instead).
  const viewCertificate = async () => {
    if (!issuedCert?.id) return;
    try {
      const { data } = await axios.get(`${API}/doserne/certificates/${issuedCert.id}/pdf`, {
        ...authHeader(), responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      setError(t.error);
    }
  };

  // Continue to the next unpassed, unlocked course; else go to the courses list.
  const goToNextCourse = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/kurs`, authHeader());
      const list = data.courses || [];
      const next = list.find(
        (c) => c.code !== code && !c.locked && c.progress?.status !== "passed",
      );
      navigate(next ? `/doserne/kurs/${next.code}` : "/doserne/kurs");
    } catch {
      navigate("/doserne/kurs");
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/doserne/kurs/${code}/acknowledgement`,
        { accepted: true, typed_name: typedName.trim(), signature_data_url: signature, lang },
        authHeader()
      );
      setIssuedCert(data.certificate || null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DoserneShell>
        <div className="max-w-4xl mx-auto p-6"><Loader2 className="animate-spin" /></div>
      </DoserneShell>
    );
  }

  // If already completed → show the "issued" screen
  if (issuedCert) {
    return (
      <DoserneShell>
        <div className="max-w-3xl mx-auto p-4 sm:p-6">
          <Link to={`/doserne/kurs/${code}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm" data-testid="ack-back-link">
            <ArrowLeft className="w-4 h-4" /> {t.backToCourse}
          </Link>
          <div className="mt-6 rounded-3xl bg-white border border-slate-200 shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 text-emerald-600 mb-6">
              <Award className="w-10 h-10" />
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-600 mb-2">{t.doneKicker}</div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 mb-6">{t.doneTitle}</h1>
            <div className="text-sm text-slate-500 mb-1">{t.course}</div>
            <div className="text-lg font-bold text-slate-900 mb-4">{pick(issuedCert.course_title, lang)}</div>
            <div className="text-xs font-mono text-slate-500 mb-6">{issuedCert.cert_number}</div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button" onClick={viewCertificate}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-bold shadow-sm"
                data-testid="view-cert-pdf-btn"
              >
                <Award className="w-4 h-4" /> {t.viewCert}
              </button>
              <button
                type="button" onClick={() => navigate("/doserne/kurs")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-white border border-slate-300 text-slate-800 font-bold hover:bg-slate-50"
                data-testid="go-courses-btn"
              >
                <GraduationCap className="w-4 h-4" /> {t.goCourses}
              </button>
              <button
                type="button" onClick={goToNextCourse}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold shadow-sm"
                data-testid="next-course-btn"
              >
                {t.nextCourse} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </DoserneShell>
    );
  }

  const scorePct = Math.round(((status?.score) || 0) * 100);

  return (
    <DoserneShell>
      <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="course-ack-page">
        <Link to={`/doserne/kurs/${code}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
          <ArrowLeft className="w-4 h-4" /> {t.backToCourse}
        </Link>

        {/* Header */}
        <div className="mt-6 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 sm:p-8">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-orange-300 mb-2">{t.kicker}</div>
          <h1 className="text-2xl sm:text-3xl font-black leading-tight">{t.title}</h1>
          <p className="mt-3 text-slate-300 max-w-2xl text-sm sm:text-base">{t.intro}</p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Cell label={t.course} value={pick(status?.course_title, lang)} />
            <Cell label={t.completed} value={new Date().toLocaleDateString(lang === "en" ? "en-GB" : lang === "ro" ? "ro-RO" : "no-NO")} />
            <Cell label={t.score} value={`${scorePct}%`} mono />
            <Cell label={t.version} value={status?.course_version || "—"} mono />
          </div>
        </div>

        {/* Declaration */}
        <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">{t.declLead}</div>
          <p className="text-slate-800 leading-relaxed" data-testid="ack-declaration">
            {declaration}
          </p>
          <label className="mt-6 flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
              data-testid="ack-consent-checkbox"
            />
            <span className="text-sm font-medium text-slate-900">{t.consent}</span>
          </label>
        </div>

        {/* Typed name */}
        <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
          <label className="block text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-2">
            {t.nameLabel}
          </label>
          <input
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={t.namePh}
            className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-orange-500 focus:ring-orange-500 outline-none text-base"
            data-testid="ack-typed-name"
          />
        </div>

        {/* Signature pad */}
        <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 inline-flex items-center gap-2">
              <PenSquare className="w-4 h-4" /> {t.sigLabel}
            </label>
            <button
              type="button"
              onClick={() => { setResetKey((k) => k + 1); setSignature(null); }}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900"
              data-testid="ack-clear-signature"
            >
              <RotateCcw className="w-3.5 h-3.5" /> {t.clear}
            </button>
          </div>
          <SignaturePad onChange={setSignature} resetKey={resetKey} />
          <div className="mt-2 text-xs text-slate-500">{t.sigHint}</div>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600 font-semibold" data-testid="ack-error">{error}</div>
        )}

        {/* Submit */}
        <div className="mt-6">
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={
              "w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-black text-base shadow " +
              (canSubmit
                ? "bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white"
                : "bg-slate-200 text-slate-400 cursor-not-allowed")
            }
            data-testid="ack-submit-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
            {submitting ? t.submitting : t.submit}
          </button>
        </div>
      </div>
    </DoserneShell>
  );
}

function Cell({ label, value, mono }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</span>
      <span className={"text-sm font-bold text-white " + (mono ? "font-mono" : "")}>{value}</span>
    </div>
  );
}
